import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GET, POST, PUT, DELETE } from "../../services/axios.service";
import { RootState } from "../../app/store";
import {
  DevFeedbackCapturedContext,
  DevFeedbackFilterType,
  DevFeedbackProps,
  DevFeedbackSeverity,
  DevFeedbackStatus,
} from "../../types/devFeedback";

export interface DevFeedbackState {
  capturedContext: DevFeedbackCapturedContext | null;
  isCaptureModalOpen: boolean;
  submitting: boolean;

  records: DevFeedbackProps[];
  totalRecords: number;
  page: number;
  size: number;
  filter: DevFeedbackFilterType;
  selectedFeedback: DevFeedbackProps | null;
  isDetailModalOpen: boolean;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;

  overlayEnabled: boolean;
  overlayRecords: DevFeedbackProps[];
  overlayLoading: boolean;
  overlayPagePath: string | null;
}

const defaultFilter: DevFeedbackFilterType = {
  search: null,
  status: null,
  severity: null,
  fromDate: null,
  toDate: null,
};

export const OVERLAY_VISIBLE_STORAGE_KEY = "devFeedbackOverlayVisible";

const readStoredOverlayEnabled = (): boolean => {
  try {
    return localStorage.getItem(OVERLAY_VISIBLE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const initialState: DevFeedbackState = {
  capturedContext: null,
  isCaptureModalOpen: false,
  submitting: false,

  records: [],
  totalRecords: 0,
  page: 1,
  size: 20,
  filter: { ...defaultFilter },
  selectedFeedback: null,
  isDetailModalOpen: false,
  loading: false,
  detailLoading: false,
  error: null,

  overlayEnabled: readStoredOverlayEnabled(),
  overlayRecords: [],
  overlayLoading: false,
  overlayPagePath: null,
};

type SubmitDevFeedbackPayload = DevFeedbackCapturedContext & {
  message: string;
  severity: DevFeedbackSeverity;
};

export const submitDevFeedback = createAsyncThunk(
  "submitDevFeedback",
  async (payload: SubmitDevFeedbackPayload) => {
    let response: any;
    await POST(`/v1/dev-feedback`, payload)
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

type FetchListProps = {
  page?: number;
  size?: number;
  filter?: Partial<DevFeedbackFilterType>;
};

export const fetchDevFeedbackListData = createAsyncThunk(
  "fetchDevFeedbackListData",
  async (req: FetchListProps) => {
    const params: Record<string, any> = {
      page: req.page ?? 1,
      size: req.size ?? 20,
    };
    if (req.filter) {
      Object.entries(req.filter).forEach(([key, value]) => {
        if (value !== null && value !== "") {
          params[key] = value;
        }
      });
    }
    let response: any;
    await GET(`/v1/dev-feedback`, params)
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

/**
 * Feeds the on-page overlay (FeedbackOverlayLayer) — scoped to a single
 * page_path so navigating to a different screen never shows another page's
 * notes. Kept separate from `records`/`filter` above, which belong to the
 * admin review table, so the two views can't clobber each other's state.
 */
export const fetchOverlayFeedback = createAsyncThunk(
  "fetchOverlayFeedback",
  async (req: { page_path: string }) => {
    let response: any;
    await GET(`/v1/dev-feedback`, { page: 1, size: 100, page_path: req.page_path })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchDevFeedbackById = createAsyncThunk(
  "fetchDevFeedbackById",
  async (id: number) => {
    let response: any;
    await GET(`/v1/dev-feedback/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const updateDevFeedback = createAsyncThunk(
  "updateDevFeedback",
  async (req: { id: number; status: DevFeedbackStatus; resolution_note?: string | null }) => {
    let response: any;
    await PUT(`/v1/dev-feedback/${req.id}`, {
      status: req.status,
      resolution_note: req.resolution_note ?? null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const deleteDevFeedback = createAsyncThunk(
  "deleteDevFeedback",
  async (id: number) => {
    let response: any;
    await DELETE(`/v1/dev-feedback/${id}`)
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const devFeedbackSlice = createSlice({
  name: "devFeedback",
  initialState,
  reducers: {
    openCaptureModal: (state, action: PayloadAction<DevFeedbackCapturedContext>) => {
      state.capturedContext = action.payload;
      state.isCaptureModalOpen = true;
    },
    closeCaptureModal: (state) => {
      state.isCaptureModalOpen = false;
      state.capturedContext = null;
    },
    setFilter: (state, action: PayloadAction<Partial<DevFeedbackFilterType>>) => {
      state.filter = { ...state.filter, ...action.payload };
      state.page = 1;
    },
    resetFilter: (state) => {
      state.filter = { ...defaultFilter };
      state.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setSize: (state, action: PayloadAction<number>) => {
      state.size = action.payload;
      state.page = 1;
    },
    openDetailModal: (state, action: PayloadAction<DevFeedbackProps | null>) => {
      state.selectedFeedback = action.payload;
      state.isDetailModalOpen = true;
    },
    closeDetailModal: (state) => {
      state.isDetailModalOpen = false;
      state.selectedFeedback = null;
    },
    setOverlayEnabled: (state, action: PayloadAction<boolean>) => {
      state.overlayEnabled = action.payload;
      try {
        localStorage.setItem(OVERLAY_VISIBLE_STORAGE_KEY, action.payload ? "1" : "0");
      } catch {
        // localStorage unavailable (private mode, etc.) — toggle still works for this session
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(submitDevFeedback.pending, (state) => {
      state.submitting = true;
    });
    builder.addCase(submitDevFeedback.fulfilled, (state) => {
      state.submitting = false;
    });
    builder.addCase(submitDevFeedback.rejected, (state) => {
      state.submitting = false;
    });

    builder.addCase(fetchDevFeedbackListData.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchDevFeedbackListData.fulfilled, (state, action: any) => {
      state.loading = false;
      if (action.payload?.data?.status === "success") {
        state.records = action.payload.data.data.records ?? [];
        state.totalRecords = action.payload.data.data.total_records ?? 0;
        state.error = null;
      } else {
        state.records = [];
        state.totalRecords = 0;
        state.error =
          action?.payload?.data?.message ||
          "Something went wrong. Please try again.";
      }
    });
    builder.addCase(fetchDevFeedbackListData.rejected, (state, action: any) => {
      state.loading = false;
      state.records = [];
      state.totalRecords = 0;
      state.error =
        action?.payload?.data?.message ||
        "Something went wrong. Please try again.";
    });

    builder.addCase(fetchOverlayFeedback.pending, (state, action) => {
      state.overlayLoading = true;
      // Clear immediately (not just on success) so a page change never shows
      // a flash of the previous page's notes while the new fetch is in flight.
      state.overlayRecords = [];
      state.overlayPagePath = action.meta.arg.page_path;
    });
    builder.addCase(fetchOverlayFeedback.fulfilled, (state, action: any) => {
      state.overlayLoading = false;
      // Ignore a response that resolved after the user already navigated
      // away again — only accept it if it's still for the page we're on.
      if (action.meta.arg.page_path !== state.overlayPagePath) return;
      if (action.payload?.data?.status === "success") {
        const records: DevFeedbackProps[] = action.payload.data.data.records ?? [];
        state.overlayRecords = records.filter(
          (r) => r.status === "open" || r.status === "in_progress"
        );
      } else {
        state.overlayRecords = [];
      }
    });
    builder.addCase(fetchOverlayFeedback.rejected, (state, action) => {
      state.overlayLoading = false;
      if (action.meta.arg.page_path === state.overlayPagePath) {
        state.overlayRecords = [];
      }
    });

    builder.addCase(fetchDevFeedbackById.pending, (state) => {
      state.detailLoading = true;
    });
    builder.addCase(fetchDevFeedbackById.fulfilled, (state, action: any) => {
      state.detailLoading = false;
      if (action.payload?.data?.status === "success") {
        state.selectedFeedback = action.payload.data.data ?? null;
        state.isDetailModalOpen = true;
      }
    });
    builder.addCase(fetchDevFeedbackById.rejected, (state) => {
      state.detailLoading = false;
    });

    builder.addCase(updateDevFeedback.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        const updated = action.payload.data.data as DevFeedbackProps;
        state.records = state.records.map((r) => (r.id === updated.id ? updated : r));
        if (state.selectedFeedback?.id === updated.id) {
          state.selectedFeedback = updated;
        }
        // Keep the on-page overlay in sync too — a note resolved/closed from
        // its own quick-review modal should vanish immediately, not linger
        // until the next toggle/page revisit.
        if (updated.status === "open" || updated.status === "in_progress") {
          state.overlayRecords = state.overlayRecords.map((r) => (r.id === updated.id ? updated : r));
        } else {
          state.overlayRecords = state.overlayRecords.filter((r) => r.id !== updated.id);
        }
      }
    });

    builder.addCase(deleteDevFeedback.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        const meta = action.meta.arg;
        state.records = state.records.filter((r) => r.id !== meta);
        state.overlayRecords = state.overlayRecords.filter((r) => r.id !== meta);
        state.totalRecords = Math.max(0, state.totalRecords - 1);
        if (state.selectedFeedback?.id === meta) {
          state.isDetailModalOpen = false;
          state.selectedFeedback = null;
        }
      }
    });
  },
});

export const {
  openCaptureModal,
  closeCaptureModal,
  setFilter,
  resetFilter,
  setPage,
  setSize,
  openDetailModal,
  closeDetailModal,
  setOverlayEnabled,
} = devFeedbackSlice.actions;

export const getCapturedContext = (state: RootState) => state.devFeedback.capturedContext;
export const getIsCaptureModalOpen = (state: RootState) => state.devFeedback.isCaptureModalOpen;
export const getIsSubmittingFeedback = (state: RootState) => state.devFeedback.submitting;

export const getDevFeedbackRecords = (state: RootState) => state.devFeedback.records;
export const getDevFeedbackTotalRecords = (state: RootState) => state.devFeedback.totalRecords;
export const getDevFeedbackPage = (state: RootState) => state.devFeedback.page;
export const getDevFeedbackSize = (state: RootState) => state.devFeedback.size;
export const getDevFeedbackFilter = (state: RootState) => state.devFeedback.filter;
export const getDevFeedbackLoading = (state: RootState) => state.devFeedback.loading;
export const getDevFeedbackError = (state: RootState) => state.devFeedback.error;
export const getSelectedDevFeedback = (state: RootState) => state.devFeedback.selectedFeedback;
export const getIsDevFeedbackDetailOpen = (state: RootState) => state.devFeedback.isDetailModalOpen;

export const getOverlayEnabled = (state: RootState) => state.devFeedback.overlayEnabled;
export const getOverlayRecords = (state: RootState) => state.devFeedback.overlayRecords;
export const getOverlayLoading = (state: RootState) => state.devFeedback.overlayLoading;

export default devFeedbackSlice.reducer;
